import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, CheckCircle2, Clock, XCircle, RefreshCw, Cloud, Trash2 } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { ConfirmDialog } from "@/components/ConfirmDialog";

const KINDS = [
  { value: "campaign", label: "Campaign (invite / promo)" },
  { value: "registration", label: "Registration" },
  { value: "confirmation", label: "Confirmation" },
  { value: "reminder", label: "Reminder" },
];

const EMPTY_FORM = {
  name: "",
  language: "en",
  category: "MARKETING",
  body: "",
  kind: "campaign",
  push_to_jalpi: false,
};

export default function Templates() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  async function load() {
    const { data } = await api.get("/templates");
    setItems(data);
  }
  useEffect(() => {
    load();
  }, []);

  async function submit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/templates", form);
      toast.success(
        form.push_to_jalpi
          ? "Template submitted to Jalpi — awaiting WhatsApp approval"
          : "Template created",
      );
      setOpen(false);
      setForm(EMPTY_FORM);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  }

  async function syncFromJalpi() {
    setSyncing(true);
    try {
      const { data } = await api.post("/templates/sync-jalpi");
      toast.success(
        `Synced ${data.total} template${data.total === 1 ? "" : "s"} — ` +
          `${data.created} added, ${data.updated} updated` +
          (data.removed ? `, ${data.removed} removed (no longer in Jalpi)` : ""),
      );
      if (data.removed) {
        toast.warning(
          `Removed: ${data.removed_names.join(", ")}` +
            (data.cleared_reminders
              ? ` — cleared ${data.cleared_reminders} reminder reference(s) pointing to them`
              : ""),
          { duration: 12000 },
        );
      }
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSyncing(false);
    }
  }

  async function setApproval(t, s) {
    try {
      await api.patch(`/templates/${t.id}`, { approval_status: s });
      toast.success(`Marked ${s}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  async function deleteTemplate(t) {
    try {
      const { data } = await api.delete(`/templates/${t.id}`);
      toast.success(
        `Deleted "${t.name}"` +
          (data.cleared_reminders
            ? ` — cleared ${data.cleared_reminders} reminder reference(s) pointing to it`
            : ""),
      );
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  return (
    <div className="mx-auto max-w-7xl p-6 md:p-10" data-testid="templates-page">
      <PageHeader
        overline="Messaging"
        title="Templates"
        subtitle="Approved templates power outbound campaigns and the registration workflow."
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={syncFromJalpi}
              disabled={syncing}
              data-testid="sync-templates-btn"
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${syncing ? "animate-spin" : ""}`} />
              {syncing ? "Syncing…" : "Sync from Jalpi"}
            </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-template-btn">
                <Plus className="mr-2 h-4 w-4" /> New template
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>Create template</DialogTitle>
                <DialogDescription>
                  Draft the message body with {"{{variables}}"} — approved templates power campaigns and workflows.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-4" data-testid="new-template-form">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Name</Label>
                    <Input
                      required
                      placeholder="seminar_invite"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      data-testid="template-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Kind</Label>
                    <Select
                      value={form.kind}
                      onValueChange={(v) => setForm({ ...form, kind: v })}
                    >
                      <SelectTrigger data-testid="template-kind-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {KINDS.map((k) => (
                          <SelectItem key={k.value} value={k.value}>
                            {k.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Language</Label>
                    <Input
                      value={form.language}
                      onChange={(e) => setForm({ ...form, language: e.target.value })}
                      data-testid="template-lang-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Category</Label>
                    <Select
                      value={form.category}
                      onValueChange={(v) => setForm({ ...form, category: v })}
                    >
                      <SelectTrigger data-testid="template-cat-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="MARKETING">Marketing</SelectItem>
                        <SelectItem value="UTILITY">Utility</SelectItem>
                        <SelectItem value="AUTHENTICATION">Authentication</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Body (use variables like {"{{name}}"}, {"{{event}}"})</Label>
                  <Textarea
                    rows={5}
                    required
                    value={form.body}
                    onChange={(e) => setForm({ ...form, body: e.target.value })}
                    data-testid="template-body-input"
                    placeholder="Hi {{name}}, join us at {{event}} on {{date}}."
                  />
                </div>
                <div className="flex items-start justify-between rounded-lg border border-border bg-muted/40 p-3">
                  <div className="pr-4">
                    <div className="flex items-center gap-1.5 text-sm font-semibold">
                      <Cloud className="h-3.5 w-3.5" /> Submit to Jalpi for approval
                    </div>
                    <div className="mt-0.5 text-xs text-muted-foreground">
                      Creates the template in WhatsApp too. Variables are converted to
                      positional ({"{{1}}"}, {"{{2}}"}…) as Meta requires. Approval can take
                      up to 24h. Leave off to draft locally.
                    </div>
                  </div>
                  <Switch
                    checked={form.push_to_jalpi}
                    onCheckedChange={(v) => setForm({ ...form, push_to_jalpi: v })}
                    data-testid="template-push-switch"
                  />
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={saving} data-testid="template-submit-btn">
                    {saving ? "Saving…" : form.push_to_jalpi ? "Create & submit" : "Create"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        }
      />

      <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {items.length === 0 && (
          <Card className="col-span-full border-border p-12 text-center">
            <div className="mx-auto max-w-sm text-sm text-muted-foreground">
              No templates yet. Create your registration / confirmation / reminder templates and mark
              approved ones ready to send.
            </div>
          </Card>
        )}
        {items.map((t) => (
          <Card
            key={t.id}
            className="relative overflow-hidden border-border p-5 pt-6 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md"
            data-testid={`template-card-${t.id}`}
          >
            <div
              className={`absolute inset-x-0 top-0 h-1.5 ${
                t.approval_status === "approved"
                  ? "bg-fun-green"
                  : t.approval_status === "rejected"
                    ? "bg-fun-red"
                    : "bg-fun-yellow"
              }`}
            />
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-1.5">
                  <div className="font-heading text-base font-semibold">{t.name}</div>
                  {t.source === "jalpi" && (
                    <Cloud
                      className="h-3.5 w-3.5 text-muted-foreground"
                      title={`From Jalpi${t.jalpi_status ? ` · ${t.jalpi_status}` : ""}`}
                    />
                  )}
                </div>
                <div className="mt-0.5 text-[11px] uppercase tracking-widest text-muted-foreground">
                  {t.kind} · {t.category} · {t.language}
                </div>
              </div>
              <ApprovalBadge status={t.approval_status} />
            </div>
            <p className="mt-4 whitespace-pre-wrap text-sm leading-relaxed text-foreground/85">
              {t.body}
            </p>
            {t.variables?.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-1">
                {t.variables.map((v) => (
                  <Badge key={v} variant="outline" className="rounded-full font-mono text-[10px]">
                    {"{{" + v + "}}"}
                  </Badge>
                ))}
              </div>
            )}
            <div className="mt-4 flex gap-2">
              {t.approval_status !== "approved" && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setApproval(t, "approved")}
                  data-testid={`approve-template-${t.id}`}
                >
                  <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" /> Mark approved
                </Button>
              )}
              {t.approval_status !== "rejected" && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setApproval(t, "rejected")}
                  data-testid={`reject-template-${t.id}`}
                >
                  <XCircle className="mr-1.5 h-3.5 w-3.5" /> Reject
                </Button>
              )}
              <ConfirmDialog
                testId={`delete-template-dialog-${t.id}`}
                title={`Delete "${t.name}"?`}
                description={
                  t.source === "jalpi"
                    ? "This only removes it from the CRM, not from Jalpi/WhatsApp — if it's " +
                      "still live there, a future sync will bring it back. Any event or " +
                      "reminder set to use it will be cleared. This cannot be undone."
                    : "Any event or reminder set to use it will be cleared. This cannot be undone."
                }
                onConfirm={() => deleteTemplate(t)}
                trigger={
                  <Button
                    size="sm"
                    variant="ghost"
                    title="Delete template"
                    className="ml-auto text-destructive hover:bg-destructive/10 hover:text-destructive"
                    data-testid={`delete-template-${t.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                }
              />
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

function ApprovalBadge({ status }) {
  const map = {
    approved: { cls: "bg-fun-green-tint text-fun-green", icon: CheckCircle2 },
    pending: { cls: "bg-fun-yellow-tint text-fun-yellow", icon: Clock },
    rejected: { cls: "bg-fun-red-tint text-fun-red", icon: XCircle },
  };
  const cfg = map[status] || map.pending;
  const Icon = cfg.icon;
  return (
    <Badge className={`${cfg.cls} rounded-full`}>
      <Icon className="mr-1 h-3 w-3" /> {status}
    </Badge>
  );
}
